import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { InboundWebhookConnectionForm } from "src/inboundWebhookNode/types";

type FieldMappingProps = {
  dataLoc?: string;
  runFieldErrors: FieldErrors | undefined;
  immutable: boolean;
  title: string;
  helpText: string;
  fieldPrefix: string;
} & (
  | {
      fieldIdKey: "correlation_id.id";
      fieldExpressionKey: "correlation_id.expression";
    }
  | {
      fieldIdKey: "response.id";
      fieldExpressionKey: "response.mapped_to";
    }
);

const FieldMapping: React.FC<FieldMappingProps> = ({
  dataLoc,
  runFieldErrors,
  immutable,
  title,
  helpText,
  fieldPrefix,
  fieldIdKey,
  fieldExpressionKey,
}) => {
  const formProps = useFormContext<InboundWebhookConnectionForm>();
  const fieldId = formProps.getValues(fieldIdKey);
  const isSearchHighlighted = useIsFieldHighlighted(fieldId);
  const runFieldError: string | undefined =
    runFieldErrors?.[formProps.getValues(fieldIdKey)];
  return (
    <>
      <h3 className="font-inter-medium-12px text-gray-800">{title}</h3>
      <span className="font-inter-normal-12px text-gray-500">{helpText}</span>
      <Card className="mt-2.5 flex w-full flex-row items-center">
        <div className="mr-2 flex font-inter-normal-12px text-gray-700">
          {fieldPrefix}
        </div>
        <Controller
          control={formProps.control}
          name={fieldExpressionKey}
          render={({ field }) => (
            <AutocompleteCodeInput
              dataLoc={dataLoc}
              disabled={immutable}
              error={runFieldError}
              isSearchHighlighted={isSearchHighlighted}
              prefix="data."
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Card>
    </>
  );
};

type Props = { runFieldErrors: FieldErrors | undefined; immutable: boolean };

export const RequestMapping: React.FC<Props> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <FieldMapping
      dataLoc="in-webhook-node-request-field-mapping"
      fieldExpressionKey="correlation_id.expression"
      fieldIdKey="correlation_id.id"
      fieldPrefix="Identifier"
      helpText="Specify where the identifier can be found in the data object. This is the same identifier set during the Connection configuration"
      immutable={immutable}
      runFieldErrors={runFieldErrors}
      title="Which field in the data object contains an identifier?"
    />
  );
};

export const ResumeRequestMapping: React.FC<Props> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <FieldMapping
      dataLoc="in-webhook-node-request-field-mapping"
      fieldExpressionKey="correlation_id.expression"
      fieldIdKey="correlation_id.id"
      fieldPrefix="Step ID"
      helpText="Specify where the step ID can be found in the data object"
      immutable={immutable}
      runFieldErrors={runFieldErrors}
      title="Which field in the data object contains the step ID?"
    />
  );
};

export const ResponseMapping: React.FC<Props> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <FieldMapping
      dataLoc="in-webhook-node-response-field-mapping"
      fieldExpressionKey="response.mapped_to"
      fieldIdKey="response.id"
      fieldPrefix="Output location"
      helpText="Add the entire webhook response into the data object"
      immutable={immutable}
      runFieldErrors={runFieldErrors}
      title="Set output location"
    />
  );
};

export const ResumeResponseMapping: React.FC<Props> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <FieldMapping
      dataLoc="in-webhook-node-response-field-mapping"
      fieldExpressionKey="response.mapped_to"
      fieldIdKey="response.id"
      fieldPrefix="Output location"
      helpText="Add the elements response into the data object"
      immutable={immutable}
      runFieldErrors={runFieldErrors}
      title="Set output location"
    />
  );
};
